import React, { useState } from 'react';
import { Card, CardContent, Typography, Button, Chip, CircularProgress, useTheme, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConnectWhatsAppOficialModal from './ConnectWhatsAppOficialModal';
import ConfirmDisconnectModal from '../common/ConfirmDisconnectModal';
import WhatsAppConfigModal from '../admin/WhatsAppConfigModal';
import { WhatsAppConfig } from '../../utils/agentApi';
import { Agent, OficialStatus } from '../../types/agent';
import { agentApi } from '../../utils/agentApi';

interface WhatsAppOficialCardProps {
  agentId: string;
  agent?: Agent | null;
  onSuccess?: () => void;
  onAgentUpdate?: (agent: Agent) => void;
  status?: {
    isConnected: boolean;
    isLoading: boolean;
    isCheckingStatus: boolean;
    phoneNumber: string | null;
    status?: OficialStatus;
  };
  onDisconnect?: () => Promise<void>;
  isWhatsAppNativeConnected?: boolean;
  isZApiConnected?: boolean;
}

const WhatsAppOficialCard: React.FC<WhatsAppOficialCardProps> = ({ 
  agentId, 
  agent,
  onSuccess, 
  onAgentUpdate,
  status,
  onDisconnect,
  isWhatsAppNativeConnected = false,
  isZApiConnected = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  const isOficialConnected = agent?.whatsappConnected === 'oficial' || status?.isConnected;
  
  const isDisabled = isOficialConnected ? false : (isWhatsAppNativeConnected || isZApiConnected);
  const isLoading = status?.isLoading || false;
  const isCheckingStatus = status?.isCheckingStatus || false;
  const phoneNumber = status?.phoneNumber || null;
  const oficialStatus = status?.status;

  const isUsableStatus = oficialStatus && oficialStatus !== 'disconnected';
  const isConnected = status?.isConnected || isUsableStatus || false;

  const getChipLabel = () => {
    if (isDisabled) return t('agents.whatsapp.disabled');
    if (oficialStatus === 'connected') return t('agents.whatsapp.connected');
    if (oficialStatus === 'pending') return t('agents.whatsapp.status.pending');
    if (oficialStatus === 'flagged') return t('agents.whatsapp.status.flagged');
    if (oficialStatus === 'restricted') return t('agents.whatsapp.status.restricted');
    if (oficialStatus === 'banned') return t('agents.whatsapp.status.banned');
    if (isConnected) return t('agents.whatsapp.connected');
    return t('agents.whatsapp.disconnected');
  };

  const getStatusTooltip = (): string => {
    if (isDisabled) return t('agents.whatsapp.statusTooltip.disabled');
    if (!oficialStatus) {
      return isConnected 
        ? t('agents.whatsapp.statusTooltip.connected')
        : t('agents.whatsapp.statusTooltip.disconnected');
    }
    
    switch (oficialStatus) {
      case 'connected':
        return t('agents.whatsapp.statusTooltip.connected');
      case 'disconnected':
        return t('agents.whatsapp.statusTooltip.disconnected');
      case 'pending':
        return t('agents.whatsapp.statusTooltip.pending');
      case 'flagged':
        return t('agents.whatsapp.statusTooltip.flagged');
      case 'restricted':
        return t('agents.whatsapp.statusTooltip.restricted');
      case 'banned':
        return t('agents.whatsapp.statusTooltip.banned');
      default:
        return t('agents.whatsapp.statusTooltip.disabled');
    }
  };

  const handleConnect = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalSuccess = () => {
    setModalOpen(false);
    onSuccess?.();
  };

  const handleConfigureClick = () => {
    setConfigModalOpen(true);
  };

  const handleConfigSave = async (config: WhatsAppConfig) => {
    if (!agent) return;

    setIsSavingConfig(true);
    try {
      const updatedAgent = await agentApi.updateWhatsAppConfig(agentId, config);
      if (onAgentUpdate) {
        onAgentUpdate(updatedAgent);
      }
      setConfigModalOpen(false);
    } catch (error) {
      console.error('Erro ao salvar configuração WhatsApp:', error);
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleDisconnect = async () => {
    setConfirmOpen(false);
    if (onDisconnect) {
      await onDisconnect();
    }
  };

  return (
    <>
      <Card
        sx={{
          maxWidth: 720,
          opacity: isDisabled ? 0.6 : 1,
          filter: isDisabled ? 'grayscale(0.3)' : 'none',
          '&:hover': {
            opacity: isDisabled ? 0.7 : 1,
          }
        }}
      >
        <CardContent>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ color: isConnected ? '#25D366' : '#9ca3af' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </div>
              <div>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isDisabled ? 'text.disabled' : 'inherit' }}>
                  {t('agents.whatsapp.official')}
                </Typography>
                <Typography variant="body2" color={isDisabled ? 'text.disabled' : 'text.secondary'}>
                  {isDisabled 
                    ? (isWhatsAppNativeConnected ? t('agents.whatsapp.disabledWhatsAppUnofficial') : t('agents.whatsapp.disabledZApi'))
                    : isConnected 
                      ? t('agents.whatsapp.connectedWith', { phone: phoneNumber || t('agents.whatsapp.phoneNumberNotAvailable') })
                      : t('agents.whatsapp.disconnected')}
                </Typography>
              </div>
            </div>            
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isCheckingStatus && <CircularProgress size={16} />}
              <Tooltip title={getStatusTooltip()} arrow>
                <Chip
                  label={getChipLabel()}
                  color='success'
                  size="small"
                  sx={isDisabled ? {
                    backgroundColor: 'grey.300',
                    color: 'grey.600'
                  } : {
                    color: theme.palette.mode === 'dark' ? '#000' : undefined
                  }}
                />
              </Tooltip>
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isDisabled ? (
              <Button
                variant="contained"
                disabled
                sx={{
                  backgroundColor: 'grey.300',
                  color: 'grey.500',
                  '&:hover': {
                    backgroundColor: 'grey.300',
                  }
                }}
              >
                {isWhatsAppNativeConnected ? t('agents.whatsapp.whatsappConnected') : t('agents.whatsapp.zApiConnected')}
              </Button>
            ) : isConnected ? (
              <>
                <Button 
                  variant="outlined"
                  color="error" 
                  onClick={() => setConfirmOpen(true)} 
                  disabled={isLoading}
                >
                  {isLoading ? t('agents.whatsapp.disconnecting') : t('agents.whatsapp.disconnect')}
                </Button>
                <Button 
                  variant="outlined"
                  onClick={handleConfigureClick} 
                  disabled={isLoading}
                >
                  {t('agents.whatsapp.configure')}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleConnect}
                disabled={isLoading}
                sx={{
                  color: theme.palette.mode === 'dark' ? '#000' : undefined
                }}
              >
                {t('agents.whatsapp.connect')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <ConnectWhatsAppOficialModal
        open={modalOpen}
        onClose={handleModalClose}
        agentId={agentId}
        onSuccess={handleModalSuccess}
      />

      <WhatsAppConfigModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        agent={agent || null}
        onSave={handleConfigSave}
        isLoading={isSavingConfig}
      />

      <ConfirmDisconnectModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDisconnect}
        isLoading={isLoading}
        description={t('agents.whatsapp.disconnectCard.description')}
      />
    </>
  );
};

export default WhatsAppOficialCard;
